<template>
  <h2 v-if="title" :class="$style.title">
    {{ title }}
  </h2>
  <nav
    :id="stickyNavId"
    ref="stickyNav"
    :class="[$style.sticky, shouldBeSticky && $style.isSticky]"
  >
    <ul :class="$style.links">
      <li
        v-for="(navItem, index) in config"
        :key="index"
        @click="(e) => scrollToItem(navItem.anchorName, e)"
      >
        <a :class="$style.linkItem" :href="`#${navItem.anchorName}`">
          <Icon v-if="navItem.icon" :name="navItem.icon" :size="isBigButton ? 'xLarge' : 'large'" />
          <img v-if="navItem.image" alt="" :class="$style.image" :src="navItem.image" />
          <span>{{ navItem.title }}</span>
        </a>
      </li>
    </ul>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, onBeforeMount } from 'vue';
import Icon from '../Icon/Icon.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  scrollContainerId: {
    type: String,
    default: '',
  },
  stickyNavId: {
    type: String,
    default: '',
  },
  bigButtons: {
    type: Boolean,
    default: false,
  },
  config: {
    type: Object,
    default: () => {},
  },
});

const buffer = 5;

const stickyNav = ref(null);
const shouldBeSticky = ref(false);
const pageHeader = ref(null);
const sectionsArray = ref(null);
const offset = ref(null);
const spyOffset = ref(null);
const scrollPositionRecord = ref(document.body.getBoundingClientRect().top);
const currentScrollPosition = ref(0);
const isScrollingUp = ref(false);
const isScrollingDown = ref(false);
const startScrollArea = ref(null);
const endScrollArea = ref(null);
const isBigButton = ref(props.bigButtons ? true : false);

const getHeaderComponent = () => {
  pageHeader.value =
    document.querySelector('.PageHeader1XP__headerContainer') ||
    document.getElementById('pageHeader').childNodes[0];
};

const updateHash = (id) => {
  const url = new URL(window.location.href);
  url.hash = id;
  history.replaceState(null, '', url.toString());
};

const scrollToItem = (id, e) => {
  if (e) {
    e.preventDefault();
  }

  const element = document.getElementById(id);
  const parent = element.parentNode;
  const siblings = parent.children;

  for (const sibling of siblings) {
    sibling.style.scrollMarginTop = 0;
  }

  setOffsets();

  if (element) {
    // timeOut is needed to let the header show on scroll up & recalculate the scroll position of the target
    setTimeout(() => {
      const anchorPosition = element.getBoundingClientRect().top + window.scrollY + buffer;
      const isNewPositionHigher = Math.abs(scrollPositionRecord.value) > anchorPosition;
      const scrollPosition = isNewPositionHigher
        ? anchorPosition - (stickyNav.value?.offsetHeight || 0) - (offset.value || 0)
        : anchorPosition - (stickyNav.value?.offsetHeight || 0);

      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }, 50);
  }
  updateHash(id);
};

const setOffsets = () => {
  const bodyTag = document.body;
  offset.value = pageHeader.value ? pageHeader.value.offsetHeight : 0;

  currentScrollPosition.value = bodyTag.getBoundingClientRect().top;
  isScrollingUp.value =
    Math.abs(currentScrollPosition.value) < Math.abs(scrollPositionRecord.value);
  isScrollingDown.value =
    Math.abs(currentScrollPosition.value) > Math.abs(scrollPositionRecord.value);

  if (isScrollingUp.value) {
    stickyNav.value.style.top = offset.value + 'px';
    spyOffset.value = offset.value + stickyNav.value.offsetHeight;
  } else {
    stickyNav.value.style.top = 0;
    spyOffset.value = stickyNav.value.offsetHeight;
  }

  if (isScrollingDown.value === isScrollingUp.value) {
    return;
  }

  scrollPositionRecord.value = bodyTag.getBoundingClientRect().top;
};

const initScrollSpy = () => {
  setOffsets();

  for (const section in sectionsArray.value) {
    const bottomMargin = parseInt(
      window.getComputedStyle(sectionsArray.value[section]).marginBottom,
      10,
    );

    const node = stickyNav.value.querySelector(
      'a[href*=' + sectionsArray.value[section].id + ']',
    )?.parentNode;

    const elementBounding = sectionsArray.value[section].getBoundingClientRect();

    if (
      elementBounding.top <= spyOffset.value + buffer &&
      elementBounding.bottom + bottomMargin >= spyOffset.value + buffer
    ) {
      node?.classList.add('active');
    } else {
      node?.classList.remove('active');
    }
  }
};

const findScrollContainerBoundaries = (scrollContainer) => {
  const headingsHeight = scrollContainer.querySelector('.ScrollNavigation__title');

  if (headingsHeight) {
    startScrollArea.value =
      scrollContainer.getBoundingClientRect().top + headingsHeight.offsetHeight;
  } else {
    startScrollArea.value = scrollContainer.getBoundingClientRect().top;
  }

  endScrollArea.value = scrollContainer.getBoundingClientRect().bottom;
};

watch(currentScrollPosition, (newValue, oldValue) => {
  const scrollContainer = document.getElementById(props.scrollContainerId);
  const scrollContainerParent = scrollContainer.parentElement;

  findScrollContainerBoundaries(scrollContainerParent ? scrollContainerParent : scrollContainer);
  if (newValue !== oldValue) {
    if (
      startScrollArea.value <= (isScrollingUp.value ? offset.value : 0) &&
      endScrollArea.value > 0
    ) {
      shouldBeSticky.value = true;
    } else {
      shouldBeSticky.value = false;
    }
  }
});

watch(shouldBeSticky, (newValue, oldValue) => {
  const scrollContainer = document.getElementById(props.scrollContainerId);

  if (newValue !== oldValue && newValue) {
    scrollContainer.style.marginTop =
      (isScrollingUp.value ? spyOffset.value : stickyNav.value.getBoundingClientRect().height) +
      'px';
  } else {
    scrollContainer.style.marginTop = 0;
  }
});

watch(pageHeader, (newValue, oldValue) => {
  if (newValue !== oldValue && newValue) {
    setOffsets();
  }
});

onBeforeMount(() => {
  window.addEventListener('resize', setOffsets);
  window.addEventListener('scroll', initScrollSpy);
  window.addEventListener('headerMounted', getHeaderComponent);
});

onMounted(() => {
  const scrollContainer = document.getElementById(props.scrollContainerId);
  const sectionsList = scrollContainer.querySelectorAll('section');

  sectionsArray.value = Array.from(sectionsList);

  setOffsets();
});

onUnmounted(() => {
  window.removeEventListener('resize', setOffsets);
  window.removeEventListener('scroll', initScrollSpy);
  window.removeEventListener('headerMounted', getHeaderComponent);
});
</script>

<style module lang="scss">
@forward './ScrollNavigation.scss';
</style>
